import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Lock, Loader2, CheckCircle2, XCircle, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg, money, Pill } from "../lib/market";

/** Simulates the payment provider's hosted checkout page (SANDBOX — no real money moves). */
export function SandboxCheckout() {
  const { providerRef } = useParams();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { http.get(`/sandbox-provider/checkout/${providerRef}`).then(({ data }) => setInfo(data)).catch((e) => toast.error(errMsg(e))); }, [providerRef]);
  const finish = async (outcome) => {
    setBusy(true);
    try {
      await http.post(`/sandbox-provider/checkout/${providerRef}/complete`, { outcome });
      nav(`/payment/return?payment_id=${info.payment_id}&engagement_id=${info.engagement_id}`);
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  if (!info) return <main className="min-h-screen bg-[#f4f5f7]" />;
  return (
    <main className="min-h-screen bg-[#f4f5f7] text-[#111] flex items-center justify-center p-6" data-testid="sandbox-checkout-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8">
        <div className="flex items-center justify-between mb-6">
          <span className="font-montserrat text-xs uppercase tracking-widest text-[#555]">Sandbox Pay</span>
          <span className="inline-flex items-center gap-1 text-[10px] font-montserrat uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-1 rounded-full"><FlaskConical className="w-3 h-3" /> Test mode</span>
        </div>
        <div className="font-inter text-sm text-[#666]">{info.merchant}</div>
        <div className="font-inter text-base mt-1">{info.service_name}</div>
        <div className="text-4xl font-semibold mt-4 tabular-nums" data-testid="sandbox-amount">{money(info.amount, info.currency)}</div>
        <div className="mt-6 space-y-1.5 font-inter text-sm text-[#555] border-t pt-4">
          <div className="flex justify-between"><span>Lawyer professional fee</span><span>{money(info.breakdown.professional_fee_minor)}</span></div>
          <div className="flex justify-between"><span>Platform fee</span><span>{money(info.breakdown.platform_fee_minor)}</span></div>
          <div className="flex justify-between"><span>VAT on platform fee</span><span>{money(info.breakdown.tax_minor)}</span></div>
        </div>
        {info.status !== "requires_action" ? <p className="mt-6 font-inter text-sm" data-testid="sandbox-already">This checkout is already {info.status}.</p> : (
          <div className="mt-8 space-y-3">
            <div className="rounded-lg border p-3 font-mono text-sm text-[#444]">4242 4242 4242 4242 · 12/30 · 123</div>
            <button disabled={busy} onClick={() => finish("success")} data-testid="sandbox-pay-success" className="w-full bg-[#0A0A0A] text-white rounded-lg py-3 font-montserrat text-sm flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Pay {money(info.amount)}</button>
            <button disabled={busy} onClick={() => finish("fail")} data-testid="sandbox-pay-fail" className="w-full border rounded-lg py-3 font-montserrat text-sm text-[#a33]">Simulate declined card</button>
          </div>)}
        <p className="font-inter text-[11px] text-[#999] mt-6">Simulated provider page. The platform only marks the payment as confirmed after receiving a signed provider webhook.</p>
      </div>
    </main>
  );
}

/** Return page: never trusts the redirect — polls the server for provider-confirmed status. */
export function PaymentReturn() {
  const [params] = useSearchParams();
  const pid = params.get("payment_id");
  const eid = params.get("engagement_id");
  const [state, setState] = useState("PENDING");
  useEffect(() => {
    let n = 0;
    const tick = async () => {
      try {
        const { data } = await http.get(`/payments/${pid}`);
        setState(data.payment.state);
        if (data.payment.state === "PENDING" && n++ < 10) setTimeout(tick, 2000);
      } catch (e) { toast.error(errMsg(e)); }
    };
    tick();
  }, [pid]);
  const ok = !["PENDING", "FAILED", "CREATED", "CANCELLED"].includes(state);
  return (
    <main className="bg-ink text-white min-h-screen flex items-center justify-center p-6" data-testid="payment-return-page">
      <div className="text-center max-w-md">
        {state === "PENDING" ? <Loader2 className="w-12 h-12 animate-spin text-turquoise mx-auto" /> : ok ? <CheckCircle2 className="w-14 h-14 text-turquoise mx-auto" /> : <XCircle className="w-14 h-14 text-red-300 mx-auto" />}
        <h1 className="font-playfair text-3xl mt-6">{state === "PENDING" ? "Awaiting provider confirmation…" : ok ? "Payment confirmed." : "Payment not completed."}</h1>
        <div className="mt-4"><Pill value={state} testId="payment-return-state" /></div>
        <Link to={`/engagements/${eid}`} className="btn-aj-primary mt-8" data-testid="payment-return-engagement">Go to engagement</Link>
      </div>
    </main>
  );
}
